(function (global) {
  'use strict';
  // Keep this calculation independent of rendering so combination boundaries
  // can be verified without adding a shopper's selection to their cart.
  function bestOffer(item, data, rate, now = Date.now()) {
    const total = item.price * item.quantity;
    let best = { total, final: total, saving: 0, couponSaving: 0, bankSaving: 0, rule: null };
    const consider = (rule, couponSaving, useBank) => {
      const bankSaving = useBank ? Math.floor((total - couponSaving) * rate / 100) : 0;
      const saving = couponSaving + bankSaving;
      if (saving > best.saving) best = { total, final: total - saving, saving, couponSaving, bankSaving, rule };
    };
    if (rate > 0) consider(null, 0, true);
    if (!data || !Number.isFinite(Date.parse(data.updatedAt)) || now - Date.parse(data.updatedAt) > 9 * 86400000) return best;
    for (const rule of data.rules || []) {
      const result = global.MPBestOffer.calculate([item], { ...data, rules: [rule], shipping: [] }, now).best;
      if (!result) continue;
      consider(rule, result.saving, false);
      if (rate > 0 && rule.combinesWithBank) consider(rule, result.saving, true);
    }
    return best;
  }
  global.MPPurchaseOfferMath = { bestOffer };
  if (!global.document) return;
  const instances = new WeakSet();
  const de = document.documentElement.lang.startsWith('de');
  const text = (german, english) => de ? german : english;
  const money = cents => new Intl.NumberFormat(de ? 'de-DE' : 'en-IE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  const couponMoney = cents => new Intl.NumberFormat(de ? 'de-DE' : 'en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);

  function rating(root) {
    const link = root.querySelector('[data-mp-product-rating]');
    if (!link) return;
    const hydrate = () => {
      const source = document.querySelector('[data-testid="trustbadge-floating-reviews-container"]');
      const scoreText = [...(source?.querySelectorAll('span') || [])].map(node => node.textContent.trim()).find(value => /^\d+[,.]\d+$/.test(value));
      const score = Number(scoreText?.replace(',', '.'));
      if (!(score >= 1 && score <= 5)) return false;
      const count = source.querySelector('[data-testid="compact-rating-count"]')?.textContent?.trim().replace(/[()]/g, '') || '';
      const scoreNode = link.querySelector('[data-mp-product-rating-score]');
      scoreNode.textContent = `${scoreText} / 5`; scoreNode.hidden = false;
      link.querySelector('[data-mp-product-rating-stars]').hidden = false;
      link.querySelector('[data-mp-product-rating-fill]').style.width = `${score * 20}%`;
      const countNode = link.querySelector('[data-mp-product-rating-count]');
      countNode.textContent = count ? count + ' ' : ''; countNode.hidden = !count;
      link.title = text('Aktuelle Shopbewertungen für Mini Power bei Trusted Shops', 'Current Mini Power shop reviews on Trusted Shops');
      return true;
    };
    if (hydrate()) return;
    const observer = new MutationObserver(() => { if (hydrate()) observer.disconnect(); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
  }

  function init() {
    document.querySelectorAll('[data-mp-product-root]').forEach(root => {
      if (instances.has(root)) return;
      instances.add(root); rating(root);
      const box = root.querySelector('[data-mp-purchase-offer]');
      const form = root.querySelector('form[action*="/cart/add"]');
      if (!box || !form) return;
      const buybox = root.querySelector('[data-mp-buybox]');
      let data = null, current = null, signature = '', queued = false;
      try {
        const seed = JSON.parse(root.querySelector('[data-mp-offer-snapshot]')?.textContent || 'null');
        if (seed?.version === 1 && seed.currency === 'EUR') data = seed;
      } catch (_) { /* Fall back to the current versioned JSON below. */ }
      const identities = JSON.parse(root.querySelector('[data-mp-offer-identities]')?.textContent || '[]');
      const variants = JSON.parse(root.querySelector('[data-mp-product-variants]')?.textContent || '[]');
      const write = (selector, value) => { box.querySelector(selector).textContent = value; };
      const render = () => {
        queued = false;
        try {
          const input = form.querySelector('[name="id"]');
          const variant = variants.find(value => String(value.id) === String(input?.value));
          // Never wait for country or VAT. If the exact checkout SKU is already
          // known, keep the coupon's original SKU restrictions and catalog price.
          const mapped = global.MiniPowerTax?.getMappedVariantId(input?.value) || input?.value;
          const identity = identities.find(value => String(value.id) === String(mapped));
          const price = identity?.price;
          const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
          const currency = box.dataset.currency;
          if (!identity || !variant?.available || input.disabled || !Number.isSafeInteger(price) || price <= 0 || !Number.isSafeInteger(quantity) || quantity < 1 || currency !== 'EUR' || buybox.dataset.mpVariantAvailability === 'unavailable' || buybox.dataset.mpEmailOnly === 'true') {
            box.hidden = true; current = null; signature = ''; return;
          }
          // Match the exact ID used by the existing tax-aware cart builder.
          const item = { productId: String(identity.productId), variantId: String(mapped), collections: identity.collections.map(String), price, quantity };
          const result = bestOffer(item, data, 0);
          current = result;
          if (!result.rule || result.couponSaving <= 0) { box.hidden = true; signature = ''; return; }
          const next = JSON.stringify([mapped, price, quantity, result.couponSaving, result.rule.code]);
          if (next === signature) return;
          signature = next;
          box.hidden = false;
          const rule = result.rule;
          write('[data-mp-offer-saving]', couponMoney(Math.round(result.couponSaving / quantity)));
          write('[data-mp-offer-code]', rule.code);
          const copy = box.querySelector('[data-mp-offer-copy]');
          copy.setAttribute('aria-label', text(`Code kopieren: ${rule.code}`, `Copy code: ${rule.code}`));
          write('[data-mp-offer-copy-label]', text('Code kopieren', 'Copy code'));
          const conditions = [];
          if (rule.newsletter) conditions.push(text('Nach bestätigter Newsletter-Anmeldung', 'After confirmed newsletter signup'));
          if (rule.once) conditions.push(text('Einmal pro Kunde', 'Once per customer'));
          if (rule.limited) conditions.push(text('Begrenzte Einlösungen', 'Limited redemptions'));
          if (rule.minSubtotal) conditions.push(text(`Ab ${money(rule.minSubtotal)} für berechtigte Artikel`, `From ${money(rule.minSubtotal)} of eligible items`));
          if (rule.minQuantity) conditions.push(text(`Ab ${rule.minQuantity} berechtigten Artikeln`, `From ${rule.minQuantity} eligible items`));
          if (rule.endsAt) conditions.push(text(`Gültig bis ${new Date(rule.endsAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}`, `Valid until ${new Date(rule.endsAt).toLocaleString('en-GB', { timeZone: 'Europe/Berlin' })}`));
          const conditionNode = box.querySelector('[data-mp-offer-conditions]');
          conditionNode.textContent = conditions.join(' · ');
          conditionNode.hidden = !conditions.length;
        } catch (_) { box.hidden = true; current = null; signature = ''; }
      };
      const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(render); } };
      box.querySelector('[data-mp-offer-copy]').addEventListener('click', async () => {
        if (!current?.rule) return;
        const code = current.rule.code;
        try { await navigator.clipboard.writeText(code); write('[data-mp-offer-status]', text('Rabattcode kopiert', 'Discount code copied')); write('[data-mp-offer-copy-label]', text('Kopiert', 'Copied')); }
        catch (_) { write('[data-mp-offer-status]', text(`Bitte Code ${code} kopieren.`, `Please copy code ${code}.`)); }
      });
      root.addEventListener('change', schedule);
      root.addEventListener('input', schedule);
      ['mp:product-variant-change', 'mp:tax-role-changed', 'currency:update', 'mp:variant-state-ready'].forEach(name => document.addEventListener(name, schedule));
      if (!data) fetch(box.dataset.offersUrl, { cache: 'force-cache', signal: AbortSignal.timeout(10000) })
        .then(response => { if (!response.ok) throw Error('Offers unavailable'); return response.json(); })
        .then(value => { if (value.version !== 1 || value.currency !== 'EUR') throw Error('Invalid offers'); data = value; schedule(); })
        .catch(() => schedule());
      render();
      [300, 1000, 3000].forEach(delay => setTimeout(schedule, delay));
      document.addEventListener('visibilitychange', schedule);
      const timer = setInterval(() => { if (!root.isConnected) clearInterval(timer); else schedule(); }, 60000);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})(typeof window === 'undefined' ? globalThis : window);

